import React, { useEffect, useState } from 'react';

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [shareSupported, setShareSupported] = useState(false);
  const [minimized, setMinimized] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (isStandalone) return;

    const hideHelp = localStorage.getItem('hideIosInstallHelp') === '1';

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    const ua = navigator.userAgent || navigator.vendor || window.opera;
    const isIos = /iphone|ipad|ipod/i.test(ua);
    if (isIos) {
      if (!hideHelp) setVisible(true);
      setShareSupported(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
    };
  }, []);

  // Auto-minimize the banner after a short time to avoid being intrusive
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (!isStandalone && visible) {
      const t = setTimeout(() => setMinimized(true), 7000);
      return () => clearTimeout(t);
    }
  }, [visible]);

  if (!visible) return null;

  const ua = navigator.userAgent || navigator.vendor || window.opera;
  const isIos = /iphone|ipad|ipod/i.test(ua);

  const handleInstall = async () => {
    try {
      if (!deferredPrompt) {
        if (isIos) {
          if (shareSupported) {
            try {
              await navigator.share({
                title: document.title,
                text: 'Agrega esta app a tu pantalla de inicio',
                url: window.location.href,
              });
            } catch (_) {
              setShowIosHelp(true);
            }
          } else {
            setShowIosHelp(true);
          }
        }
        return;
      }
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setVisible(false);
      }
      setDeferredPrompt(null);
    } catch (_) {
      // ignore
    }
  };

  const expandFromMinimized = () => setMinimized(false);

  const ShareIcon = ({ className = '' }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 3a1 1 0 0 1 1 1v8.586l2.293-2.293a1 1 0 1 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4A1 1 0 1 1 8.707 10.293L11 12.586V4a1 1 0 0 1 1-1Z"/>
      <path d="M5 15a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-4Z"/>
    </svg>
  );

  return (
    <div className={`fixed ${minimized ? 'bottom-6 right-4' : 'top-20 right-4'} z-50`}>
      {minimized ? (
        <button
          onClick={expandFromMinimized}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-emerald-600 rounded-full shadow-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          aria-label="Abrir instalación"
        >
          <ShareIcon className="w-4 h-4 text-white animate-pulse" />
          Instalar
        </button>
      ) : (
        <div className="bg-white shadow-lg rounded-xl border border-gray-200 w-[320px] max-w-[90vw]">
          <div className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900">Instalar app</h3>
                {isIos ? (
                  <p className="mt-1 text-xs text-gray-600">
                    Para acceso rápido, toca compartir y luego “Agregar a inicio”.
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-gray-600">
                    Instala esta app en tu pantalla de inicio para un acceso más rápido.
                  </p>
                )}
              </div>
              <button
                onClick={() => setVisible(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Cerrar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 0 1 1.414 0L10 8.586l4.293-4.293a1 1 0 1 1 1.414 1.414L11.414 10l4.293 4.293a1 1 0 0 1-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 0 1-1.414-1.414L8.586 10 4.293 5.707a1 1 0 0 1 0-1.414Z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={handleInstall}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {isIos && <ShareIcon className="w-4 h-4 text-white animate-pulse" />}
                {deferredPrompt ? 'Instalar app' : (isIos ? (shareSupported ? 'Abrir menú compartir' : 'Cómo instalar') : 'Instalar app')}
              </button>
              {isIos && (
                <button
                  onClick={() => setShowIosHelp((v) => !v)}
                  className="text-xs text-gray-600 hover:text-gray-800"
                >
                  {showIosHelp ? 'Ocultar ayuda' : 'Ver cómo instalar'}
                </button>
              )}
            </div>
          </div>

          {showIosHelp && (
            <div className="fixed inset-0 z-50">
              <div className="absolute inset-0 bg-black/40" onClick={() => setShowIosHelp(false)} />
              <div className="absolute left-1/2 -translate-x-1/2 bottom-6 w-[92%] max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200 p-4">
                <h3 className="text-base font-semibold text-gray-800 mb-2">Cómo instalar en iPhone/iPad</h3>
                <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
                  <li>Abre este sitio en Safari.</li>
                  <li>Toca el botón <strong>Compartir</strong> (cuadrado con flecha hacia arriba).</li>
                  <li>Desliza y elige <strong>“Agregar a pantalla de inicio”</strong>.</li>
                  <li>Confirma el nombre y toca <strong>Añadir</strong>.</li>
                </ol>
                <div className="flex items-center justify-between mt-3">
                  <label className="flex items-center gap-2 text-xs text-gray-600">
                    <input type="checkbox" onChange={(e) => {
                      localStorage.setItem('hideIosInstallHelp', e.target.checked ? '1' : '0');
                    }} />
                    No volver a mostrar
                  </label>
                  <button onClick={() => setShowIosHelp(false)} className="text-blue-600 hover:underline text-sm">Entendido</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
